import { createHttp, type Http, type HttpOptions } from "./http";
import { ApiError } from "./errors";
import { BadShape } from "./models/common";
import { accountEndpoints } from "./endpoints/account";
import { consultEndpoints } from "./endpoints/consult";
import { dailyEndpoints } from "./endpoints/daily";
import { profileEndpoints } from "./endpoints/profile";
import { socialEndpoints } from "./endpoints/social";

/**
 * The typed client for every backend route the app uses.
 *
 * Composed from endpoint groups so each file stays small; callers see one
 * flat object. Every method rejects with an {@link ApiError} — a body that
 * does not match its schema included, so the UI handles one failure shape.
 */
export type ApiClient = ReturnType<typeof profileEndpoints> &
  ReturnType<typeof dailyEndpoints> &
  ReturnType<typeof accountEndpoints> &
  ReturnType<typeof socialEndpoints> &
  ReturnType<typeof consultEndpoints>;

export function createApiClient(options: HttpOptions): ApiClient {
  return composeClient(createHttp(options));
}

export function composeClient(http: Http): ApiClient {
  const groups = {
    ...profileEndpoints(http),
    ...dailyEndpoints(http),
    ...accountEndpoints(http),
    ...socialEndpoints(http),
    ...consultEndpoints(http),
  };
  return Object.fromEntries(
    Object.entries(groups).map(([name, method]) => [name, wrapShapeErrors(method)]),
  ) as ApiClient;
}

type AnyMethod = (...args: never[]) => Promise<unknown>;

/** A body that failed its schema becomes a 502 ApiError, never a raw ZodError. */
function wrapShapeErrors<F extends AnyMethod>(method: F): F {
  return (async (...args: Parameters<F>) => {
    try {
      return await method(...args);
    } catch (error) {
      if (error instanceof BadShape) throw ApiError.badResponse();
      throw error;
    }
  }) as F;
}
